import { keyBy, orderBy } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { recordTrackerSubscriptionEvent } from '@/graphql/connectionHealth';
import { GraphQLSubscriptionErrors } from '@/graphql/errors';
import { useRefetchOnAppResume } from '@/hooks/useRefetchOnAppResume';
import { useReportError } from '@/hooks/useReportError';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { WorkflowTracker } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  EXCLUDED_WORKFLOW_TRACKER_KINDS,
  VISIBLE_WORKFLOW_TRACKER_KINDS,
} from '../constants';
import {
  type FetchCurrentUserWorkflowTrackerHistoryQuery,
  useCurrentUserWorkflowTrackerUpdatesSubscription,
  useDismissAllWorkflowTrackersMutation,
  useFetchCurrentUserWorkflowTrackerHistoryQuery,
} from '../graphql/CurrentUserWorkflowTrackersContext.generated';
import { useWorkflowTrackerDownloads } from '../hooks/useWorkflowTrackerDownloads';
import {
  CurrentUserWorkflowTrackersContext,
  type CurrentUserWorkflowTrackersContextValue,
} from './CurrentUserWorkflowTrackersContext.context';
import { WorkflowTrackerDownloadsContext } from './WorkflowTrackerDownloads.context';

const PAGE_SIZE = 50;

function useContextValue(): CurrentUserWorkflowTrackersContextValue {
  const user = useCurrentUser();
  const { reportError } = useReportError();

  const [dismissAllWorkflowTrackers] = useDismissAllWorkflowTrackersMutation();

  // Live cache of workflow trackers that powers the UI
  const [trackersById, setTrackersById] = useState<
    Record<string, WorkflowTracker>
  >({});

  // Pagination state
  const [pageInfo, setPageInfo] = useState<
    FetchCurrentUserWorkflowTrackerHistoryQuery['currentUserWorkflowTrackerHistory']['pageInfo']
  >({
    hasNextPage: true,
    endCursor: null,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // Use refs to access current values without causing callback recreations
  const pageInfoRef = useRef(pageInfo);
  const loadingMoreRef = useRef(loadingMore);

  useEffect(() => {
    pageInfoRef.current = pageInfo;
  }, [pageInfo]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const { loading, refetch, fetchMore } =
    useFetchCurrentUserWorkflowTrackerHistoryQuery({
      skip: !user,
      variables: {
        first: PAGE_SIZE,
        after: undefined,
        kinds: VISIBLE_WORKFLOW_TRACKER_KINDS,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const list = getNodes(data.currentUserWorkflowTrackerHistory);
        const newTrackersById = keyBy(list, 'id') as Record<
          string,
          WorkflowTracker
        >;
        setTrackersById(newTrackersById);
        if (data.currentUserWorkflowTrackerHistory.pageInfo) {
          setPageInfo(data.currentUserWorkflowTrackerHistory.pageInfo);
        }
      },
      onError: (error) => {
        reportError('Failed to fetch workflow trackers', error as Error);
      },
    });

  const loadOlder = useCallback(async () => {
    if (!pageInfoRef.current.hasNextPage || loadingMoreRef.current) return;

    setLoadingMore(true);
    try {
      const { data } = await fetchMore({
        context: {
          // for some reason, fetchMore queries don't seem to default to scalar parsing
          forceParseScalars: true,
        },
        variables: { after: pageInfoRef.current.endCursor },
      });

      const incoming = getNodes(data.currentUserWorkflowTrackerHistory);
      setTrackersById((prev) => ({
        ...prev,
        ...(keyBy(incoming, 'id') as Record<string, WorkflowTracker>),
      }));
      setPageInfo(data.currentUserWorkflowTrackerHistory.pageInfo);
    } catch (error) {
      reportError('Failed to load older workflow trackers', error as Error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, reportError]);

  // SUBSCRIBE TO WORKFLOW TRACKER UPDATES
  const { restart } = useCurrentUserWorkflowTrackerUpdatesSubscription({
    skip: !user,
    onData: ({ data }) => {
      if (!data.data?.currentUserWorkflowTrackerUpdates) return;

      recordTrackerSubscriptionEvent('update');
      const tracker = data.data
        .currentUserWorkflowTrackerUpdates as WorkflowTracker;

      if (EXCLUDED_WORKFLOW_TRACKER_KINDS.includes(tracker.kind)) return;

      diagnostics.debug(
        '[WorkflowTrackersProvider] New tracker update received',
        {
          trackerId: tracker.id,
          trackerKind: tracker.kind,
          trackerStatus: tracker.status,
          createdAt: tracker.createdAt,
        }
      );

      // Add/update tracker in existing state
      setTrackersById((prev) => ({
        ...prev,
        [tracker.id]: tracker,
      }));
    },
    onError: (error) => {
      recordTrackerSubscriptionEvent('error');
      const errorMessage = error.message || JSON.stringify(error);

      if (errorMessage.includes(GraphQLSubscriptionErrors.RECONNECT_MESSAGE)) {
        recordTrackerSubscriptionEvent('restart');
        diagnostics.debug(
          'Workflow tracker updates subscription reconnecting, refetching workflow trackers'
        );
        // restart the subscription to ensure we don't miss any workflow trackers
        restart();
        // refetch to ensure we have the latest workflow trackers
        void refetch();
      } else {
        diagnostics.error(
          'Failed to subscribe to workflow tracker updates',
          error
        );
      }
    },
    onComplete: () => recordTrackerSubscriptionEvent('complete'),
  });

  const dismissAll = useCallback(async () => {
    await dismissAllWorkflowTrackers();
    await refetch();
  }, [dismissAllWorkflowTrackers, refetch]);

  // REFETCH ON APP RESUME
  useRefetchOnAppResume(refetch, {
    label: 'CurrentUserWorkflowTrackers',
    enabled: !!user,
  });

  // DERIVED HELPERS
  const trackers = useMemo(
    () =>
      orderBy(
        Object.values(trackersById),
        'createdAt',
        'desc'
      ) as WorkflowTracker[],
    [trackersById]
  );

  return {
    trackers,
    byId: trackersById,
    refetch,
    loadOlder,
    hasMore: pageInfo.hasNextPage,
    loading,
    loadingMore,
    dismissAll,
  };
}

export function CurrentUserWorkflowTrackersContextProvider({
  children,
}: PropsWithChildren) {
  const value = useContextValue();
  const user = useCurrentUser();
  const downloads = useWorkflowTrackerDownloads(user?.id, value.trackers);
  return (
    <CurrentUserWorkflowTrackersContext.Provider value={value}>
      <WorkflowTrackerDownloadsContext.Provider value={downloads}>
        {children}
      </WorkflowTrackerDownloadsContext.Provider>
    </CurrentUserWorkflowTrackersContext.Provider>
  );
}
